import React, { useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import { CircularProgress, Dialog } from "@mui/material";
import { FormikProps } from "formik";
import { toast } from "react-toastify";
import {
  IdaEntityType,
  IdaGenerateMode,
  useIdaGenerateMutation,
} from "../../../../graphql/types";
import {
  CHAMBER_DESCRIPTION_CHARACTER_LIMIT,
  ChamberProfileFormValues,
} from "./manageChamberProfileModel";

type ChamberDescriptionFieldProps = {
  formik: FormikProps<ChamberProfileFormValues>;
  isSandbox: boolean;
};

const ChamberDescriptionField = ({
  formik,
  isSandbox,
}: ChamberDescriptionFieldProps): React.ReactElement => {
  const [generatedByIda, setGeneratedByIda] = useState(false);
  const [generatedText, setGeneratedText] = useState("");
  const [previewOpen, setPreviewOpen] = useState(false);
  const [generateDescription, { loading: generating }] =
    useIdaGenerateMutation();
  const description = formik.values.description || "";
  const descriptionError =
    formik.touched.description && formik.errors.description
      ? String(formik.errors.description)
      : undefined;

  const handleGenerate = async () => {
    try {
      const { data } = await generateDescription({
        variables: {
          input: {
            entityName: formik.values.name || "the chamber",
            entityType: IdaEntityType.Chamber,
            mode: IdaGenerateMode.CompanyDescription,
            keyDetails: description.trim(),
            maxLength: CHAMBER_DESCRIPTION_CHARACTER_LIMIT,
          },
        },
      });

      if (!data?.idaGenerate.success || !data.idaGenerate.generatedText) {
        toast.error("IDA could not generate a chamber description");
        return;
      }

      setGeneratedText(
        data.idaGenerate.generatedText
          .trim()
          .slice(0, CHAMBER_DESCRIPTION_CHARACTER_LIMIT),
      );
      setPreviewOpen(true);
    } catch {
      toast.error("IDA could not generate a chamber description");
    }
  };

  const handleUseGeneratedDescription = async () => {
    await formik.setFieldValue("description", generatedText);
    await formik.setFieldTouched("description", true, false);
    setGeneratedByIda(true);
    setPreviewOpen(false);
    toast.success("Chamber Description Updated Successfully!");
  };

  return (
    <>
      <div className="flex h-full min-w-0 flex-col">
        <label
          htmlFor="description"
          className="mb-2 block text-base font-semibold text-black"
        >
          Chamber Description
        </label>
        <p className="mb-[10px] text-sm leading-6 text-[#454B58]">
          Use our Intelligent Digital Assistant to generate your chamber
          description. Simply enter content or your website URL, then ask IDA to
          create a customized version that highlights any specific details you
          want.
        </p>
        <div
          className={`flex min-h-[180px] flex-1 flex-col rounded-lg bg-white p-4 ${
            generatedByIda
              ? "border-2 border-[#00B621]"
              : descriptionError
                ? "border border-red-600"
                : "border border-gray-900"
          }`}
        >
          {generatedByIda ? (
            <p className="mb-1 text-[10px] font-bold leading-[15px] text-[#00B621]">
              Generated Chamber Description
            </p>
          ) : null}
          <textarea
            id="description"
            name="description"
            rows={4}
            maxLength={CHAMBER_DESCRIPTION_CHARACTER_LIMIT}
            value={description}
            onBlur={formik.handleBlur}
            onChange={(event) => {
              formik.handleChange(event);
              setGeneratedByIda(false);
            }}
            placeholder="Enter Details About Your Chamber"
            className="min-h-[118px] flex-1 resize-none bg-transparent text-base text-[#333333] outline-none placeholder:text-gray-400"
          />
        </div>
        <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
          <span
            className={`text-sm leading-6 ${
              description.length > CHAMBER_DESCRIPTION_CHARACTER_LIMIT
                ? "text-red-600"
                : "text-[#454B58]"
            }`}
          >
            {description.length}/{CHAMBER_DESCRIPTION_CHARACTER_LIMIT}{" "}
            characters
          </span>
          <div className="flex items-center gap-3">
            <img
              src="/ida-chatbot-icon.png"
              alt="IDA"
              className="h-10 w-10 object-contain"
            />
            {isSandbox ? (
              <button
                type="button"
                onClick={handleGenerate}
                disabled={generating}
                className="min-h-9 rounded-lg bg-[#4CAF50] px-6 py-2 text-sm font-bold uppercase text-white transition hover:bg-[#388E3C] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {generatedByIda ? "Regenerate" : "Generate"}
              </button>
            ) : (
              <span className="whitespace-nowrap rounded-[10px] border border-[#E53935] bg-[#E53935] px-[14px] py-[6px] text-xs font-bold text-white">
                COMING SOON
              </span>
            )}
          </div>
        </div>
        {descriptionError ? (
          <p className="mt-1 text-left text-[13px] text-red-600">
            {descriptionError}
          </p>
        ) : null}
      </div>

      <Dialog open={generating} maxWidth="sm" fullWidth>
        <div className="relative rounded-[20px] bg-white px-6 pb-8 pt-5">
          <img
            src="/ida-chatbot-icon.png"
            alt="IDA"
            className="h-20 w-20 object-contain"
          />
          <h2 className="mt-2 text-lg font-bold text-cpaGold">
            Let Our Intelligent Digital Assistant Generate Your Chamber
            Description
          </h2>
          <p className="mt-2 text-[13px] leading-5 text-gray-800">
            Generating description...
          </p>
          <div className="flex min-h-[120px] items-center justify-center">
            <CircularProgress size={36} sx={{ color: "#E1A731" }} />
          </div>
        </div>
      </Dialog>

      <Dialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <div className="relative rounded-[20px] bg-white px-8 pb-7 pt-8 text-center">
          <button
            type="button"
            onClick={() => setPreviewOpen(false)}
            className="absolute right-3 top-3 flex h-[30px] w-[30px] items-center justify-center rounded-full bg-red-600 text-white hover:bg-red-700"
            aria-label="Close generated description preview"
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </button>
          <img
            src="/ida-chatbot-icon.png"
            alt="IDA"
            className="mx-auto h-24 w-24 object-contain"
          />
          <h2 className="mt-3 text-lg font-bold text-black">
            IDA has Generated Your Chamber Description.
          </h2>
          <p className="mt-2 text-sm leading-6 text-gray-800">
            Would you like to use this version or regenerate a new one?
          </p>
          <div className="mt-5 max-h-52 overflow-auto rounded-lg border border-[#00B621] p-4 text-left">
            <p className="mb-2 text-sm font-bold text-[#00B621]">
              Chamber description generated by IDA:
            </p>
            <p className="text-[13px] leading-6 text-gray-900">
              {generatedText}
            </p>
          </div>
          <div className="mt-6 flex flex-col justify-center gap-4 sm:flex-row">
            <button
              type="button"
              onClick={() => {
                setPreviewOpen(false);
                void handleGenerate();
              }}
              className="rounded-lg bg-[#00B621] px-6 py-2.5 text-sm font-bold uppercase text-white hover:bg-[#009A1B]"
            >
              RE-GENERATE
            </button>
            <button
              type="button"
              onClick={() => void handleUseGeneratedDescription()}
              className="rounded-lg bg-cpaGold px-10 py-2.5 text-sm font-bold uppercase text-white hover:bg-cpaGoldDark"
            >
              USE
            </button>
          </div>
        </div>
      </Dialog>
    </>
  );
};

export default ChamberDescriptionField;
